import { CascadiaColor, CascadiaTypographyStyle } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";

import { Icon, Loader, Text } from "@certn/cascadia-react";

export interface ActionListItemProps {
  alignItems?: "start" | "center" | "end";
  children?: React.ReactNode | string;
  displayAccessibility?: boolean;
  hoverFill?: CascadiaColor;
  isFocused?: boolean;
  isHovered?: boolean;
  isLoading?: boolean;
  leading?: React.ReactNode;
  limitLeadingSize?: boolean;
  padding?: string;
  paddingLeft?: string;
  paddingRight?: string;
  paddingTop?: string;
  tag?: React.ReactNode;
  testID?: string;
  title?: React.ReactNode | string;
  titleStyle?: CascadiaTypographyStyle;
  trailing?: React.ReactNode;
  centerAlignTrailing?: boolean;
  truncateTitle?: boolean;
  errorMessage?: string;
  className?: string;
}

export function ActionListItem(props: ActionListItemProps) {
  const {
    leading,
    isLoading,
    titleStyle = "global/heading-medium",
    testID = "action-list-item",
    limitLeadingSize = true,
    centerAlignTrailing = false,
    truncateTitle = true,
    errorMessage,
    className,
    title,
  } = props;

  return (
    <div
      className={css(
        styles.container,
        props.children && styles.containerWithChildren,
        props.leading && styles.containerWithLeading,
        props.isHovered && {
          backgroundColor: "var(--background-ghost-hover)",
        },
        props.isFocused && { boxShadow: "var(--focus-action)" },
        css({
          alignItems: props.alignItems ?? "start",
          padding: props.padding ?? undefined,
          paddingLeft: props.paddingLeft,
          paddingRight: props.paddingRight,
          paddingTop: props.paddingTop,
        }),
        className
      )}
      data-testid={testID}
    >
      {(leading || isLoading || errorMessage) && (
        <div
          className={css(
            styles.leading,
            limitLeadingSize && {
              width: "var(--space-6)",
              height: "var(--space-6)",
            }
          )}
        >
          {isLoading ? (
            <Loader size="small" type="spinner" />
          ) : leading ? (
            leading
          ) : (
            <Icon
              name="error"
              color="background-danger-strong"
              size="space-6"
            />
          )}
        </div>
      )}
      <div className={styles.content}>
        {props.tag && <div className={styles.tag}>{props.tag}</div>}
        {title && (
          <Text
            truncate={truncateTitle}
            testID={`${testID}-title`}
            style={titleStyle}
            as={props.displayAccessibility ? "h2" : undefined}
          >
            {props.title}
          </Text>
        )}
        {typeof props.children === "string" ? (
          <Text style="global/body-secondary" color="text-weak">
            {props.children}
          </Text>
        ) : (
          props.children
        )}

        {errorMessage && (
          <Text style="global/body-secondary" color="background-danger-strong">
            {errorMessage}
          </Text>
        )}
      </div>
      {props.trailing && (
        <div
          className={css(
            styles.trailing,
            centerAlignTrailing && { alignSelf: "center" }
          )}
        >
          {props.trailing}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    width: "100%",
    boxSizing: "border-box",
    gap: "var(--space-3)",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
  }),
  containerWithChildren: css({
    paddingTop: "var(--space-4)",
    paddingBottom: "var(--space-4)",
  }),
  containerWithLeading: css({
    paddingLeft: "var(--space-3)",
  }),
  tag: css({
    marginBottom: "var(--space-05)",
    display: "flex",
    flexDirection: "row",
    gap: "var(--space-2-5)",
  }),
  leading: css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "var(--border-radius-1)",
    "> img": {
      height: "100%",
      width: "100%",
    },
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    flexShrink: 1,
    overflow: "hidden",
    flexGrow: 1,
    alignSelf: "center",
  }),
  trailing: css({
    display: "flex",
    justifyContent: "end",
    alignItems: "center",
  }),
};
